<template>
  <div v-if="editor">
    <MenuBar :id="id" class="editor__header" :editor="editor">
      <template v-slot:expand-button>
        <MarkdownDialog v-model="contentFullscreen" />
      </template>
    </MenuBar>
    <EditorContent
      class="content"
      :editor="editor"
      :style="{
        maxHeight: `${contentHeight}px`,
      }"
    />
  </div>
</template>
<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import MenuBar from "./MenuBar.vue";
import Highlight from "@tiptap/extension-highlight";
import Typography from "@tiptap/extension-typography";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Picture from "@tiptap/extension-image";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import MarkdownDialog from "./MarkdownDialog.vue";

const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),

      backgroundColor: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-background-color"),
        renderHTML: (attributes) => {
          return {
            "data-background-color": attributes.backgroundColor,
            style: `background-color: ${attributes.backgroundColor}`,
          };
        },
      },
    };
  },
});

export default {
  components: {
    EditorContent,
    Highlight,
    Typography,
    MenuBar,
    Link,
    Picture,
    MarkdownDialog,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    id: {
      type: String,
      default: "",
    },
  },
  data: () => ({
    editor: null,
    dialog: false,
    content: "",
    contentFullscreen: "",
  }),
  watch: {
    value: {
      handler(value) {
          this.content = value;
      },
      immediate: true,
    },
    content(value) {
      this.contentFullscreen = value;
      const isSame = this.editor.getHTML() === value;

      if (isSame) {
        return;
      }
      this.editor.commands.setContent(value);
      this.$emit("input", value);
    },
    contentFullscreen(value) {
      const isSame = this.content === value;

      if (isSame) {
        return;
      }

      this.content = value;
    },
  },
  mounted() {
    this.editor = new Editor({
      content: this.content,
      extensions: [
        StarterKit,
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        Highlight,
        Link.configure({
          openOnClick: false,
        }),
        TextStyle,
        Color,
        Picture.configure({
          inline: true,
        }),
        Table.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        CustomTableCell,
        TaskList,
        TaskItem.configure({
          nested: true,
        }),
      ],
      onUpdate: () => {
        this.$emit("input", this.editor.getHTML());
      },
    });
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  computed: {
    contentHeight() {
      return window.innerHeight - 350;
    },
  },
};
</script>
<style>
.ProseMirror-focused {
  outline: none;
}
</style>
<style lang="scss" scoped>
.content {
  border: 1px solid #ccc;
  min-height: 300px;
  padding: 8px;
  overflow: auto;
}
.editor__header {
  position: sticky;
  background: white;
  z-index: 1;
}
.ProseMirror-focused {
  outline: none;
}

.ProseMirror-focused {
  outline: none;
}

.ProseMirror {
  margin-top: 1rem;

  > * + * {
    margin-top: 0.75em;
  }

  ul,
  ol {
    padding: 0 2rem;
  }

  ul[data-type="taskList"] {
    list-style: none;
    padding: 0;

    p {
      margin: 0;
    }

    li {
      display: flex;

      > label {
        flex: 0 0 auto;
        margin-right: 0.5rem;
        user-select: none;
      }

      > div {
        flex: 1 1 auto;
      }
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1;
  }

  code {
    background-color: rgba(#616161, 0.1);
    color: #616161;
  }

  pre {
    background: #0d0d0d;
    color: #fff;
    font-family: "JetBrainsMono", monospace;
    padding: 0.2rem;
    border-radius: 0.3rem;

    code {
      color: inherit;
      padding: 0;
      background: none;
      font-size: 0.8rem;
    }
  }

  mark {
    background-color: #faf594;
  }

  img {
    max-width: 100%;
    height: auto;

    &.ProseMirror-selectednode {
      outline: 3px solid #68cef8;
    }
  }

  blockquote {
    padding-left: 1rem;
    border-left: 2px solid rgba(#0d0d0d, 0.1);
  }

  a {
    color: #68cef8;
  }

  p {
    margin: 4px 0;
  }
}

.ProseMirror {
  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    overflow: hidden;

    td,
    th {
      min-width: 1em;
      border: 2px solid #ced4da;
      padding: 3px 5px;
      vertical-align: top;
      box-sizing: border-box;
      position: relative;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      font-weight: bold;
      text-align: left;
      background-color: #f1f3f5;
    }

    .selectedCell:after {
      z-index: 2;
      position: absolute;
      content: "";
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      background: rgba(200, 200, 255, 0.4);
      pointer-events: none;
    }

    .column-resize-handle {
      position: absolute;
      right: -2px;
      top: 0;
      bottom: -2px;
      width: 4px;
      background-color: #adf;
      pointer-events: none;
    }

    p {
      margin: 0;
    }
  }
}

.tableWrapper {
  overflow-x: auto;
}

.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}
</style>
